import posthog from 'posthog-js'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import {
  postQuoteEngagement,
  type QuoteEngagement,
  QuoteEngagementKind,
} from '../../models/insuranceApplication'
import {
  getTotalPremium,
  type PricedQuoteCard,
  PricingKind,
  type QuoteCardData,
  QuoteCardStage,
} from './quoteCardTypes'

/**
 * Everything the Quotes page reports to PostHog. One namespace (`insure_quotes_`/`insure_quote_`)
 * so the whole surface can be filtered together in insights and in session replay.
 */
export enum QuotesEvent {
  PAGE_VIEWED = 'insure_quotes_page_viewed',
  EMPTY_STATE_SHOWN = 'insure_quotes_empty_state_shown',
  ALL_DECLINED_SHOWN = 'insure_quotes_all_declined_shown',
  LINK_COPIED = 'insure_quotes_link_copied',

  SORT_CHANGED = 'insure_quotes_sort_changed',
  FILTER_OPENED = 'insure_quotes_filter_opened',
  FILTER_CLOSED = 'insure_quotes_filter_closed',
  FILTER_TOGGLED = 'insure_quotes_filter_toggled',
  FILTER_CLEARED = 'insure_quotes_filter_cleared',
  FILTERS_CLEARED_ALL = 'insure_quotes_filters_cleared_all',

  CAROUSEL_SCROLLED = 'insure_quotes_carousel_scrolled',

  CARD_OPENED = 'insure_quote_card_opened',
  CARD_CLOSED = 'insure_quote_card_closed',
  CARD_SECTION_TOGGLED = 'insure_quote_card_section_toggled',
  CARD_INFO_OPENED = 'insure_quote_card_info_opened',
  CARD_DOCUMENTS_OPENED = 'insure_quote_card_documents_opened',
  CARD_RESTRICTIONS_OPENED = 'insure_quote_card_restrictions_opened',
  CARD_PROVIDE_INFO_CLICKED = 'insure_quote_card_provide_info_clicked',
  CARD_ATTESTATION_OPENED = 'insure_quote_card_attestation_opened',
  CARD_ATTESTATION_ACCEPTED = 'insure_quote_card_attestation_accepted',
  CARD_DELETED = 'insure_quote_card_deleted',

  COMPARISON_TOGGLED = 'insure_quotes_comparison_toggled',
  COMPARISON_REMOVED = 'insure_quotes_comparison_removed',

  CART_ITEM_ADDED = 'insure_quotes_cart_item_added',
  CART_ITEM_REMOVED = 'insure_quotes_cart_item_removed',
  CART_OPENED = 'insure_quotes_cart_opened',
  CART_CLOSED = 'insure_quotes_cart_closed',
  CHECKOUT_REQUESTED = 'insure_quotes_checkout_requested',
  CHECKOUT_SUCCEEDED = 'insure_quotes_checkout_succeeded',
  CHECKOUT_FAILED = 'insure_quotes_checkout_failed',
  CHECKOUT_CONFIRMATION_DISMISSED = 'insure_quotes_checkout_confirmation_dismissed',

  SUMMARY_SECTION_OPENED = 'insure_quotes_summary_section_opened',
  SUMMARY_SECTION_CLOSED = 'insure_quotes_summary_section_closed',
  SUMMARY_TOGGLED = 'insure_quotes_summary_toggled',
  SUMMARY_CONNECT_PROVIDER_CLICKED = 'insure_quotes_summary_connect_provider_clicked',

  UPLOAD_OPENED = 'insure_quotes_upload_opened',
  UPLOAD_PARSED = 'insure_quotes_upload_parsed',
  UPLOAD_CONFIRMED = 'insure_quotes_upload_confirmed',
  UPLOAD_FAILED = 'insure_quotes_upload_failed',

  /** The customer clicked to turn on desktop notifications; `permission` is the browser's answer. */
  NOTIFICATIONS_REQUESTED = 'insure_quotes_notifications_requested',
  NOTIFICATION_OPENED = 'insure_quotes_notification_opened',

  // Named by the spec's success metrics, so outside the insure_quotes_ namespace.
  REVIEW_ATTESTATION_SHOWN = 'insure_review_attestation_shown',
  REVIEW_ATTESTATION_ACCEPTED = 'insure_review_attestation_accepted',
  CUSTOMER_LINK_OPENED = 'insure_fill_application_customer_link_opened',
}

/** Where the quotes are being read — the customer's own application, or the read-only shared link. */
export enum QuotesSurface {
  APP = 'app',
  SHARED_LINK = 'shared_link',
}

/** Stable keys for the filter bar, so renaming a label doesn't break the history behind it. */
export enum QuotesFilter {
  COVERAGES = 'coverages',
  RATING = 'rating',
  FINANCING = 'financing',
  CLAIMS_CAPABILITIES = 'claims_capabilities',
  COI_SPEED = 'coi_speed',
}

/** Stable keys for the expandable carrier-detail rows — the "beyond price" signal this page exists to read. */
export enum QuoteCardSection {
  INSURANCE_TYPE = 'insurance_type',
  FINANCING = 'financing',
  CLAIMS_CAPABILITIES = 'claims_capabilities',
  COI_SPEED = 'coi_speed',
}

/** What a coverage was added to (or removed from) the cart by. */
export enum CartItemScope {
  /** The card's single CTA, covering every coverage on it. */
  BUNDLE = 'bundle',
  /** One coverage's own CTA on an itemized card. */
  COVERAGE = 'coverage',
  /** The Remove on a carrier's row in the cart panel. */
  SIDEBAR = 'sidebar',
  /** The recommended package's CTA, reported once for each offer in it. */
  PACKAGE = 'package',
}

/** Which affordance opened the focused card. */
export enum CardOpenSource {
  CARD = 'card',
  COMPARISON_RAIL = 'comparison_rail',
}

type Properties = Record<string, unknown>

const QuotesAnalyticsContext = createContext<Properties>({})

/**
 * posthog.init only runs in production (see Root.tsx), and capture on an uninitialized
 * client logs an error per call — which this page would produce by the dozen.
 */
export function trackQuotesEvent(event: QuotesEvent, properties?: Properties) {
  reportQuoteEngagement(event, properties ?? {})
  if (!posthog.__loaded) return
  posthog.capture(event, properties)
}

/** Forwards the clicks behind the Slack engagement alerts; the backend decides whether they count. */
function reportQuoteEngagement(event: QuotesEvent, properties: Properties) {
  const { surface, application_id: applicationId, indication_id: indicationId } = properties
  if (surface !== QuotesSurface.APP || typeof applicationId !== 'string' || typeof indicationId !== 'string')
    return

  const engagement = quoteEngagementFor(event, properties)
  if (engagement) postQuoteEngagement(applicationId, indicationId, engagement)
}

function quoteEngagementFor(event: QuotesEvent, properties: Properties): QuoteEngagement | null {
  switch (event) {
    case QuotesEvent.CARD_OPENED:
      return { kind: QuoteEngagementKind.CARD_OPENED }
    case QuotesEvent.CARD_SECTION_TOGGLED:
      return properties.opened === true && typeof properties.section === 'string'
        ? { kind: QuoteEngagementKind.SECTION_OPENED, section: properties.section }
        : null
    case QuotesEvent.CARD_DOCUMENTS_OPENED:
      return { kind: QuoteEngagementKind.SECTION_OPENED, section: 'documents' }
    case QuotesEvent.CART_ITEM_ADDED:
      return { kind: QuoteEngagementKind.CART_ADDED }
    default:
      return null
  }
}

/**
 * Reports an event with everything the surrounding scopes know about it — the page it
 * happened on, and the quote card it happened inside, if any.
 */
export function useTrackQuotesEvent() {
  const scope = useContext(QuotesAnalyticsContext)
  return useCallback(
    (event: QuotesEvent, properties?: Properties) => trackQuotesEvent(event, { ...scope, ...properties }),
    [scope]
  )
}

interface QuotesAnalyticsProviderProps {
  surface: QuotesSurface
  applicationId?: string
  orgId?: string
  children: ReactNode
}

/** Page scope: put around a whole quotes surface so every event below it says which one it was. */
export function QuotesAnalyticsProvider({
  surface,
  applicationId,
  orgId,
  children,
}: QuotesAnalyticsProviderProps) {
  const scope = useMemo(
    () => ({ surface, application_id: applicationId ?? null, org_id: orgId ?? null }),
    [surface, applicationId, orgId]
  )
  return <QuotesAnalyticsContext.Provider value={scope}>{children}</QuotesAnalyticsContext.Provider>
}

/**
 * The page view, once per application. The quotes page refetches every few seconds while
 * carriers are still answering, so this can't ride a plain mount effect.
 */
export function useQuotesPageViewed(applicationId: string | undefined, properties: Properties) {
  const track = useTrackQuotesEvent()
  const viewed = useRef<string | null>(null)

  useEffect(() => {
    if (!applicationId || viewed.current === applicationId) return
    viewed.current = applicationId
    track(QuotesEvent.PAGE_VIEWED, properties)
  }, [applicationId, properties, track])
}

/** Reports a state the page fell into rather than one the customer clicked, once per mount. */
export function useQuotesStateShown(event: QuotesEvent, active: boolean, properties?: Properties) {
  const track = useTrackQuotesEvent()
  const shown = useRef(false)

  useEffect(() => {
    if (!active || shown.current) return
    shown.current = true
    track(event, properties)
  }, [event, active, properties, track])
}

/** Card scope: nests inside the page scope so a click deep in a card still names the carrier. */
export function QuoteAnalyticsScope({
  quote,
  position,
  children,
}: {
  quote: QuoteCardData
  /** Where the card sat in the list as rendered — first position is the one most quotes are read from. */
  position?: number
  children: ReactNode
}) {
  const page = useContext(QuotesAnalyticsContext)
  const scope = useMemo(() => ({ ...page, ...quoteCardProperties(quote, position) }), [page, quote, position])
  return <QuotesAnalyticsContext.Provider value={scope}>{children}</QuotesAnalyticsContext.Provider>
}

/** Stopwatch for dwell times; the clock read lives here rather than in a component's render scope. */
export function startTiming(): () => number {
  const startedAt = Date.now()
  return () => Date.now() - startedAt
}

function isPriced(quote: QuoteCardData): quote is PricedQuoteCard {
  return quote.stage === QuoteCardStage.PRICED
}

/** Who the quote is from and what it offers — attached to every event fired inside its card. */
export function quoteCardProperties(quote: QuoteCardData, position?: number): Properties {
  const base: Properties = {
    indication_id: quote.id,
    carrier_name: quote.carrierName,
    carrier_id: quote.carrierId ?? null,
    carrier_program: quote.carrierProgram ?? null,
    carrier_rating: quote.carrierRating ?? null,
    coi_speed: quote.coiSpeed ?? null,
    insurance_type: quote.insuranceType ?? null,
    sourced_through: quote.sourcedThrough ?? null,
    vendor: quote.vendor ?? null,
    stage: quote.stage,
    position: position ?? null,
  }
  if (!isPriced(quote)) return base

  return {
    ...base,
    pricing_kind: quote.pricingKind,
    indication_status: quote.indicationStatus ?? null,
    total_premium: getTotalPremium(quote),
    coverage_types: quote.coverages.map((coverage) => coverage.type),
    coverage_count: quote.coverages.length,
    is_customer_uploaded: !!quote.isCustomerUploaded,
    is_current_insurer: !!quote.isCurrentInsurer,
    is_broker_recommended: !!quote.brokerRecommended,
    is_applied: !!quote.appliedAt,
    is_superseded: !!quote.supersededAt,
    has_broker_note: !!quote.brokerNote,
    has_quote_documents: !!quote.quoteDocuments?.length,
    has_financing: !!quote.financing,
    has_claims_capabilities: !!quote.claimsCapabilities,
    has_moratorium: !!quote.moratorium,
  }
}

/** The shape of the board a customer is looking at — what was on offer when they acted. */
export function quoteListProperties(quotes: QuoteCardData[]): Properties {
  const priced = quotes.filter(isPriced)
  const premiums = priced.map(getTotalPremium)
  const countStage = (stage: QuoteCardStage) => quotes.filter((quote) => quote.stage === stage).length

  return {
    quote_count: quotes.length,
    priced_quote_count: priced.length,
    declined_quote_count: countStage(QuoteCardStage.DECLINED),
    unavailable_quote_count: countStage(QuoteCardStage.UNAVAILABLE),
    error_quote_count: countStage(QuoteCardStage.ERROR),
    pending_quote_count: countStage(QuoteCardStage.PREPARING) + countStage(QuoteCardStage.APPLICATION_SENT),
    priced_carrier_names: priced.map((quote) => quote.carrierName),
    cheapest_premium: premiums.length ? Math.min(...premiums) : null,
    most_expensive_premium: premiums.length ? Math.max(...premiums) : null,
    has_customer_uploaded_quote: priced.some((quote) => quote.isCustomerUploaded),
    has_broker_recommendation: priced.some((quote) => quote.brokerRecommended),
    has_indication_pricing: priced.some((quote) => quote.pricingKind === PricingKind.INDICATION),
  }
}
